<template>
  <div>
    <h2 class="left-panel-title">Your progress overview</h2>
    <div class="left-panel-container" :class="{ 'fade-in': isVisible }">
      <div class="progress-summary">
        <div class="summary-header">
          <div class="summary-header-main">
            <div class="summary-title">
              <h2>SUMMARY OF YOUR PROGRESS</h2>
            </div>
          </div>
        </div>

        <!-- WILL CREATION Section -->
        <WillCreation
          :expanded="expandedSections.willCreation"
          :testator="testator"
          :numberOfCompletedSteps="numberOfCompletedSteps"
          :totalSteps="totalSteps"
          :allStepsCompleted="allStepsCompleted"
          :willDetailsChanged="willDetailsChanged"
          :previewWillDetailsChanged="previewWillDetailsChanged"
          :latestVersionStatus="latestVersionStatus"
          @toggle="toggleSection"
          @willDownloaded="$emit('willDownloaded')"
          @previewWillDownloaded="$emit('previewWillDownloaded')"
        />

        <!-- VALIDATE WILL Section -->
        <VerifyWill
          v-if="showVerifyWill"
          :expanded="expandedSections.verifyWill"
          :disabled="isSectionDisabled.verifyWill"
          :completed="sections.verifyWill.completed"
          :testator="testator"
          @toggle="toggleSection"
          @willVerification="willVerification"
          @viewUploadedWill="viewUploadedWill"
        />

        <!-- SET WILL LOCATION Section -->
        <SetWillLocation
          v-if="!isMobile || sections.willCreation.completed"
          :expanded="expandedSections.willLocation"
          :disabled="isSectionDisabled.willLocation"
          :completed="sections.willLocation.completed"
          :testator="testator"
          @toggle="toggleSection"
          @willLocation="willLocation"
        />

        <!-- SEND WILL FOR STORAGE Section -->
        <SendWillForStorage
          v-if="!isMobile || sections.willCreation.completed"
          :expanded="expandedSections.willStorage"
          :disabled="isSectionDisabled.willStorage"
          :completed="sections.willStorage.completed"
          :willStorageStatus="determineWillStorageStatus"
          :willCollectionBooked="testator?.willCollectionBooked"
          :willBookingDetails="willBookingDetails"
          :testator="testator"
          @toggle="toggleSection"
          @scheduleCollection="scheduleCollection"
          @viewUploadedWill="viewUploadedWill"
        />
      </div>
    </div>
  </div>
</template>

<script>
  import WillCreation from './WillCreation';
  import VerifyWill from './VerifyWill';
  import SetWillLocation from './SetWillLocation';
  import SendWillForStorage from './SendWillForStorage';
  import { latestWillVersion } from '../../../constants/js/will-verification';
  import { verificationStatus } from '../../../constants/js/will-verification';
  import { CREATE_UPLOADED_WILL_PRESIGNED_URL_MUTATION } from '../../../constants/graphql/will-verifications';

  export default {
    name: 'LeftPanelContainer',
    components: {
      WillCreation,
      VerifyWill,
      SetWillLocation,
      SendWillForStorage,
    },
    props: {
      testator: {
        type: Object,
        required: true,
      },
      numberOfCompletedSteps: {
        type: Number,
        required: true,
        default: 0,
      },
      totalSteps: {
        type: Number,
        required: true,
        default: 0,
      },
      allStepsCompleted: {
        type: Boolean,
        required: true,
        default: false,
      },
      willDetailsChanged: {
        type: Boolean,
        required: true,
        default: false,
      },
      previewWillDetailsChanged: {
        type: Boolean,
        required: true,
        default: false,
      },
    },
    data() {
      return {
        isVisible: false, // For fade-in animation
        expandedSections: {
          willCreation: false,
          verifyWill: false,
          willLocation: false,
          willStorage: false,
        },
        sections: {
          willCreation: {
            completed: false,
            inProgress: true,
          },
          verifyWill: {
            completed: false,
          },
          willLocation: {
            completed: false,
          },
          willStorage: {
            completed: false,
          },
        },
      };
    },
    mounted() {
      // Set the current stage panel as expanded
      this.expandedSections[this.currentStagePanel] = true;

      this.$nextTick(() => {
        setTimeout(() => {
          this.isVisible = true;
        }, 111);
      });
    },
    computed: {
      showVerifyWill() {
        if (this.sections.willStorage.completed) return false;
        return !this.isMobile || this.sections.willCreation.completed;
      },
      isSectionDisabled() {
        return {
          willCreation: false,
          verifyWill: this.determineVerifyWillDisabled,
          willLocation: this.determineWillLocationDisabled,
          willStorage: this.determineWillStorageDisabled,
        };
      },
      determineVerifyWillDisabled() {
        return (
          !this.allStepsCompleted || this.testator.willVersions.length === 0
        );
      },
      determineWillLocationDisabled() {
        if (!this.allStepsCompleted) {
          return true;
        }
        if (this.latestVersion?.status.toLowerCase() !== 'valid') {
          return true;
        }
        return false;
      },

      determineWillStorageDisabled() {
        if (this.testator.dateStored) return false;
        const hasStoredVersion = (this.testator?.willVersions || []).some(
          v => v.dateStored
        );
        if (hasStoredVersion) return false;
        return (
          !this.allStepsCompleted ||
          this.latestVersion?.status.toLowerCase() !== 'valid'
        );
      },

      currentStagePanel() {
        if (this.willDetailsChanged) {
          return 'willCreation';
        }

        // On mobile, only show willCreation until it's completed
        if (this.isMobile) {
          return 'willCreation';
        }

        const panels = [
          'willCreation',
          'verifyWill',
          'willLocation',
          'willStorage',
        ];

        for (let i = 0; i < panels.length; i++) {
          const panel = panels[i];
          const isDisabled = this.isSectionDisabled[panel];
          const isCompleted = this.sections[panel]?.completed;

          if (!isDisabled && !isCompleted) {
            return panel;
          }
        }

        return 'willCreation';
      },
      latestVersion() {
        return latestWillVersion(this.testator?.willVersions);
      },
      latestVersionStatus() {
        return verificationStatus(
          this.latestVersion?.status,
          this.icons
        )?.status?.toLowerCase();
      },
      formattedDateStored() {
        if (!this.testator.dateStored) return '';
        return new Date(this.testator.dateStored).toISOString().split('T')[0];
      },
      determineWillStorageStatus() {
        if (this.testator.dateStored) {
          return `Stored in Vault - ${this.formattedDateStored}`;
        }

        if (!this.testator.activeWillCollectionBooking) {
          // A booking stops being "active" once its collection date passes, so
          // a will the courier has already delivered to the vault (but which the
          // vault team has not yet checked in) must not read as never booked.
          // The booking still counts as booked, so no Schedule button either.
          if (this.deliveredAwaitingVault) {
            return 'Delivered, awaiting vault';
          }
          return 'Not Scheduled';
        }

        const booking = this.testator.activeWillCollectionBooking;

        if (booking?.statusDisplayName === 'Cancelled') {
          return 'Cancelled';
        }

        if (booking?.collectionDetails) {
          if (booking.collectionStatus == 'collection-assigned') {
            return `Collection booked: ${booking.collectionDetails}`;
          }
        } else if (booking.collectionStatus == 'cancelled') {
          return 'Cancelled';
        } else {
          return booking.collectionDetails;
        }

        return booking?.statusDisplayName || 'Not stored';
      },
      deliveredAwaitingVault() {
        return (
          this.testator.willCollectionBooked &&
          this.testator.latestWillCollectionBooking?.collectionStatus ===
            'delivered'
        );
      },
      willBookingDetails() {
        if (!this.testator.activeWillCollectionBooking) {
          return this.deliveredAwaitingVault
            ? 'Delivered, awaiting vault'
            : 'Not Scheduled';
        }

        const booking = this.testator.activeWillCollectionBooking;

        if (booking.statusDisplayName === 'Cancelled') {
          return 'Cancelled';
        }

        return booking.collectionDetails || '';
      },
      isMobile() {
        return this.$isMobileDevice();
      },
    },
    watch: {
      currentStagePanel(newStage) {
        // Close all sections first
        Object.keys(this.expandedSections).forEach(key => {
          this.expandedSections[key] = false;
        });

        // Expand the current stage panel
        this.expandedSections[newStage] = true;
      },
      testator: {
        handler() {
          this.updateSectionCompletionStatus();
        },
        deep: true,
        immediate: true,
      },
      numberOfCompletedSteps() {
        this.updateSectionCompletionStatus();
      },
    },
    methods: {
      toggleSection(sectionKey) {
        // Don't allow toggling if section is disabled
        if (this.isSectionDisabled[sectionKey]) {
          return;
        }

        const wasExpanded = this.expandedSections[sectionKey];

        // Close all sections first
        Object.keys(this.expandedSections).forEach(key => {
          this.expandedSections[key] = false;
        });

        // Only open the clicked section if it wasn't already open
        if (!wasExpanded) {
          this.expandedSections[sectionKey] = true;
        }
      },
      willVerification() {
        this.$router.push({ name: 'WillVerification' });
      },
      viewUploadedWill(id) {
        var newTab = window.open('', '_blank');
        this.$apollo
          .mutate({
            mutation: CREATE_UPLOADED_WILL_PRESIGNED_URL_MUTATION,
            variables: { id },
          })
          .then(response => {
            newTab.location.href = response.data.createUploadedWillPresignedUrl;
          })
          .catch(() => {
            newTab.close();
          });
      },
      willLocation() {
        this.$router.push({ name: 'WillLocation' });
      },
      updateSectionCompletionStatus() {
        this.sections.willCreation.completed =
          this.numberOfCompletedSteps === this.totalSteps;

        this.sections.verifyWill.completed =
          this.latestVersion?.status.toLowerCase() === 'valid';
        this.sections.willLocation.completed =
          !!this.testator.willLocation || !!this.testator.dateStored;
        this.sections.willStorage.completed = !!this.testator.dateStored;
      },
      scheduleCollection() {
        this.$router.push({
          name: 'WillCollectionBooking',
          query: { testatorId: this.testator.id },
        });
      },
    },
  };
</script>

<style lang="scss" scoped>
  .left-panel-container {
    width: 100%;
    border-top: 6px solid #009677;
    box-shadow: 4px 20px 16px 0px rgba(0, 0, 0, 0.06);
    opacity: 0;
    transition: opacity 0.6s ease-out;

    &.fade-in {
      opacity: 1;
    }
  }

  .progress-summary {
    .summary-header {
      background: #ffffff;
      overflow: hidden;
      height: 80px; // Fixed height for summary header
      box-shadow: 4px 20px 16px 0px rgba(0, 0, 0, 0.06);
      position: relative;
      z-index: 11;
    }

    .summary-header-main {
      display: flex;
      align-items: center;
      width: 100%;
      height: 100%;
      padding: 30px;
    }

    .summary-title {
      flex: 1;

      h2 {
        font-weight: 600;
        font-size: 16px;
        line-height: 150%;
        color: #626262;
      }
    }
  }

  // Use deep selector to style child components
  ::v-deep .accordion-panel {
    background: #ffffff;
    margin-bottom: 0; // No gap between sections
    transition: height 0.6s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.5s ease;
    overflow: hidden;
    height: 80px; // Fixed height when not expanded
    border-bottom: 1px solid #dcddde;

    &:last-child {
      border-bottom: none;
    }

    &.disabled {
      background: #f5f5f5;
      opacity: 0.6;
    }

    &.completed {
      background: #f2f2f2;
    }

    // Add shadow to top of panel that follows an expanded panel
    &:not(.expanded) {
      position: relative;

      &::before {
        content: '';
        position: absolute;
        top: -20px;
        left: 0;
        right: 0;
        height: 20px;
        box-shadow: 4px 20px 16px 0px rgba(0, 0, 0, 0.06);
        opacity: 0;
        transition: opacity 0.6s cubic-bezier(0.4, 0, 0.2, 1);
        z-index: 1;
      }
    }

    // Show shadow on panel that follows expanded panel
    &.expanded + .accordion-panel:not(.expanded)::before {
      opacity: 1;
    }

    &.expanded {
      height: auto; // Allow height to expand with content
      border-bottom: none; // Remove border when expanded
    }
  }

  ::v-deep .accordion-header {
    display: flex;
    flex-direction: column;
    justify-content: center;
    height: 80px; // Match the panel height
    padding: 26px 30px; // Consistent padding
    cursor: pointer;

    &.disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }
  }

  ::v-deep .header-main {
    display: flex;
    align-items: center;
    width: 100%;
  }

  ::v-deep .header-icon {
    flex-shrink: 0;
    width: 40px;
    display: flex;
    align-items: center;
    justify-content: flex-start;
  }

  ::v-deep .header-title {
    flex: 1;
    padding: 0 16px;

    h3 {
      color: #282828;
      margin: 0;
      font-weight: 600;
      font-size: 18px;
      line-height: 150%;
      letter-spacing: 0%;
      text-transform: uppercase;
    }
  }

  ::v-deep .header-chevron {
    flex-shrink: 0;
    width: 40px;
    display: flex;
    align-items: center;
    justify-content: center;

    .v-icon {
      transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
      zoom: 1.4;

      &.rotated {
        transform: rotate(180deg);
      }
    }
  }

  ::v-deep .expanded-container {
    background-color: #ffffff;
    max-height: 0;
    overflow: hidden;
    opacity: 0;
    transform: translateY(-10px);
    transition: all 0.8s cubic-bezier(0.25, 0.8, 0.25, 1);
    visibility: hidden;

    &.expanded {
      max-height: 500px;
      opacity: 1;
      transform: translateY(0);
      visibility: visible;
    }
  }

  ::v-deep .status-container {
    margin: 0 30px 8px 30px;
    padding-top: 8px;

    .status-text {
      font-weight: 600;
      font-size: 16px;
      line-height: 150%;
      letter-spacing: 0%;
      color: #282828;

      .status-value-steps {
        color: #009677;
        font-weight: 700;
        font-size: 22px;
        line-height: 150%;
        letter-spacing: 0%;
      }

      .status-not-uploaded {
        color: #ff9800;
        font-weight: 600;
        font-size: 16px;
        line-height: 150%;
        letter-spacing: 0%;
      }

      .status-not-set {
        color: #ff9800;
        font-weight: 600;
        font-size: 16px;
        line-height: 150%;
        letter-spacing: 0%;
      }

      .status-not-scheduled {
        color: #ff9800;
        font-weight: 600;
        font-size: 16px;
        line-height: 150%;
        letter-spacing: 0%;
      }
    }
  }

  ::v-deep .accordion-content {
    background-color: #ffffff; // White background for expanded content
  }

  ::v-deep .content-wrapper {
    margin: 30px 30px 40px 30px;
    padding-top: 20px;
    background-color: #ffffff;
    border-top: 1px solid rgba(220, 221, 222, 1);
  }

  ::v-deep .section-content {
    .content-margins {
      margin-bottom: 30px;
      color: #282828;
      h4 {
        font-weight: 600;
        font-size: 18px;
        line-height: 200%;
        margin-bottom: 18px;
      }
      p {
        font-weight: 400;
        font-size: 14px;
        line-height: 120%;

        strong {
          font-weight: 600;
          font-size: 14px;
          line-height: 120%;
        }
      }
    }
  }

  ::v-deep .status-section {
    margin-bottom: 16px;

    .status-label {
      font-weight: 600;
      color: #282828;
      margin-bottom: 4px;
    }

    .status-value {
      color: #4caf50;
      font-weight: 500;
      margin: 0 0 12px 0;
    }
  }

  ::v-deep .explainer-text {
    font-style: italic;
    color: #999 !important;
    margin-bottom: 20px;
  }

  ::v-deep .action-buttons {
    display: flex;
    gap: 16px;

    margin-top: 20px;

    .upload-button {
      background-color: #4caf50 !important;
      color: white !important;
      text-transform: none;
      font-weight: 500;
    }
  }

  .left-panel-title {
    font-weight: 600;
    font-size: 24px;
    line-height: 120%;
    letter-spacing: 0px;
    color: #282828;
    margin-top: 47px;
    margin-bottom: 22px;
  }
</style>
